import * as React from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import { handleArmSite } from "./SiteAboutPageUtils/handleArmSite";
import { useNavigate } from "react-router-dom";
import { AlertState, SiteInfo } from "./SiteAboutPageUtils/siteAboutInterfaces";

type Dispatcher<S> = React.Dispatch<React.SetStateAction<S>>;

export const RebootDialog = ({
  rebootDialogOpen,
  setRebootDialogOpen,
  setSiteInfo,
  setBackdropLoadingScreenOn,
  setAlertState,
  setSyncNeeded,
  setArmedStatus,
  setArmButtonDisabled,
  hasFlexSchedule,
  setArmSiteFlexScheduleDialogOpen,
  setArmSiteFlexScheduleDialogAction,
}: {
  rebootDialogOpen: boolean;
  setRebootDialogOpen: Dispatcher<boolean>;
  setSiteInfo: Dispatcher<SiteInfo>;
  setBackdropLoadingScreenOn: Dispatcher<boolean>;
  setAlertState: Dispatcher<AlertState>;
  setSyncNeeded: Dispatcher<boolean>;
  setArmedStatus: Dispatcher<boolean>;
  setArmButtonDisabled: any;
  hasFlexSchedule: boolean;
  setArmSiteFlexScheduleDialogOpen: Dispatcher<boolean>;
  setArmSiteFlexScheduleDialogAction: Dispatcher<"arm" | "reboot">;
}) => {
  const navigate = useNavigate();
  return (
    <Dialog
      open={rebootDialogOpen}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">{"Reboot Site?"}</DialogTitle>

      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          Camera changes have been saved, and will deploy on next site arm.
          Would you like to reboot the site now and immediately deploy changes?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <OrangeTextButton
          onClick={() => {
            // Declining a reboot must not change the arming state — the site
            // stays armed and keeps running its previous settings until the
            // next scheduled arm. (Previously this optimistically set
            // armed:false, which misreported an armed site as disarmed.)
            setRebootDialogOpen(false);
            localStorage.setItem("rebootSite", "false");
          }}
          text="No"
          disabled={false}
        />
        <OrangeTextButton
          onClick={() => {
            localStorage.setItem("rebootSite", "false");
            if (hasFlexSchedule) {
              // Flex-schedule sites can't be armed site-wide: the backend
              // rejects customer/{id}/action/arm for them (silently — the
              // Max Security incident, ENG-389). Hand off to the flex
              // schedule picker, whose arm is a manual reboot of the
              // selected schedules — flagged as the reboot flavor so the
              // action log reads "Manual Reboot" (ENG-1404).
              setRebootDialogOpen(false);
              setArmSiteFlexScheduleDialogAction("reboot");
              setArmSiteFlexScheduleDialogOpen(true);
              return;
            }
            handleArmSite(
              setBackdropLoadingScreenOn,
              setSiteInfo,
              setAlertState,
              setSyncNeeded,
              setArmedStatus,
              navigate,
              setArmButtonDisabled,
              // Logged as "Manual Reboot", not "Manual Arm" (ENG-1380)
              "reboot"
            );
            setSiteInfo((previousState: SiteInfo) => {
              return { ...previousState, armed: true };
            });
            setRebootDialogOpen(false);
          }}
          text="Reboot"
          disabled={false}
        />
      </DialogActions>
    </Dialog>
  );
};
